import { CodeBlock, Fig, Figs, GuideMemo, Related } from "./memoUi";

export function ExtraTrafficMemo() {
  return (
    <GuideMemo
      sections={[
        { id: "why", label: "Зачем из ЛК" },
        { id: "link", label: "Итоговая ссылка" },
        { id: "split", label: "CPC и CPM" },
      ]}
    >
      <section className="Memos-section" id="why">
        <h2>Почему ссылка только из кабинета</h2>
        <p>
          В договоре клиент может запросить доступ в ЛК. Если весь трафик шёл по ссылкам из объявлений,
          клики уже лежат в кабинете. Иначе придётся чинить задним числом.
        </p>
        <p>
          Ведите на все объявления, даже если разметка одинаковая: креативы разные, статистика в ЛК по каждому.
          Сменили баннер — смените ссылку.
        </p>
      </section>
      <section className="Memos-section" id="link">
        <h2>Как собрать ссылку для площадки</h2>
        <ol>
          <li>В объявлении нажмите «Получить итоговую ссылку».</li>
          <li>Кабинет подставит UUID кампании и баннера. <code translate="no">{"{SITE_ID}"}</code> из итоговой ссылки уже убирается.</li>
          <li>
            <code translate="no">{"{PRG}"}</code> замените на код площадки. Для Programmatic: Bid PRG84, Rich PRG81,
            Инволтра PRG8, MyBid PRG80. Для Retargeo: PRG4, PRG5, PRG7.
          </li>
          <li>
            Если площадка отдаёт свой id источника, допишите его макрос. Наш макрос площадки в ссылку для Bid/Rich
            не оставляйте.
          </li>
        </ol>
        <Fig n={50} caption="Кнопка «Получить итоговую ссылку». Схема UTM на скрине старая — актуальная в памятке «Разметка»" />
        <Fig n={51} caption="Дальше ссылку правят под площадку: код PRG и макрос источника" />
        <p>
          Пустой UTM-контейнер оставлять нельзя. Макрос площадки не кладите внутрь наших id — см. «Разметка»
          и параметр <code translate="no">code</code>.
        </p>
        <div className="Memos-callout">
          <p>
            Первыми обычно тестируют Rich и Bid. Логины площадок — только в таблице доступов.
          </p>
        </div>
      </section>
      <section className="Memos-section" id="split">
        <h2>Если в медиаплане CPC и CPM</h2>
        <ul>
          <li>Две охватные РК и две строчки ОП.</li>
          <li>У контекстолога запросите посадочные: они могут вести не туда, куда трафик.</li>
          <li>В CPC-РК создайте объявления, скопируйте ссылки контекстологу, саму РК включите и выключите.</li>
          <li>В работу идут только ссылки из ЛК.</li>
        </ul>
        <Related
          items={[
            { to: "/admin/memos/markup", label: "Разметка" },
            { to: "/admin/memos/pop", label: "Bid, MyBid, Rich" },
            { to: "/admin/memos/retargeo", label: "Retargeo" },
          ]}
        />
      </section>
    </GuideMemo>
  );
}

export function TrackersMemo() {
  return (
    <GuideMemo
      sections={[
        { id: "wrap", label: "Пиксели" },
        { id: "dsp", label: "Наша DSP" },
        { id: "mix", label: "Смешивание каналов" },
        { id: "ymm", label: "ЯММ без DMP" },
      ]}
    >
      <section className="Memos-section" id="wrap">
        <h2>Сторонние трекеры</h2>
        <p>
          Smartis, Яндекс Медиа Метрика, Target Ads считают показы и postview у клиента.
          Ссылку перехода оборачивают в пиксель: из одной ссылки получаются две — на показ и на клик.
        </p>
        <Fig n={124} caption="Таблица обёрнутых ссылок: канал, посадочная и UTM, на выходе show и click" />
        <p>
          <code translate="no">show</code> — показ, <code translate="no">click</code> — клик.
        </p>
      </section>
      <section className="Memos-section" id="dsp">
        <h2>Как вставить в нашу DSP</h2>
        <ol>
          <li>Чат разработки, тегните РК и объявление.</li>
          <li>Прогоните тест отбивки по площадкам до распределения бюджета.</li>
          <li>Через день сверьте выгрузку клиента и ЛК.</li>
        </ol>
        <p>
          Sape, Getintent, Hybrid: пиксели показа и клика вставляются в каждое объявление на стороне площадки.
        </p>
      </section>
      <section className="Memos-section" id="mix">
        <h2>Площадки без пикселя показа</h2>
        <p>
          Bid, Rich, Инволтра показ клиенту не отдадут. Нельзя, чтобы в трекере клиента были клики PRG84
          и ноль показов PRG84: «где показ, там и клик».
        </p>
        <p>
          Рабочая схема: Sape под PRG86 с пикселями показа и клика. Когда показы пошли — Bid под тем же PRG86
          только со ссылкой на клик. Сначала Sape, потом Bid.
        </p>
        <p>
          Лучше не смешивать. Ещё лучше закрывать план своей охваткой: postview собирается с наших SSP.
          Чужие показы уменьшают наши PV.
        </p>
      </section>
      <section className="Memos-section" id="ymm">
        <h2>Клиент без DMP, но хочет PV</h2>
        <p>
          Для ЯММ в пиксель показа добавляют <code translate="no">&universal_device_id=[uadid]</code>.
          DSP подставляет IDFA, GAID и другие id устройства. Крипта ищет конверсии в Метрике на сайте.
        </p>
        <p>На сайте должен стоять счётчик Метрики. Досмотры видео из Директа ЯММ сейчас не считает.</p>
        <Related
          items={[
            { to: "/admin/memos/dsp", label: "Sape, Getintent, Hybrid" },
            { to: "/admin/memos/extra-traffic", label: "Доп. трафик" },
          ]}
        />
      </section>
    </GuideMemo>
  );
}

export function RetargeoMemo() {
  return (
    <GuideMemo
      sections={[
        { id: "role", label: "Роль трафика" },
        { id: "mod", label: "Модерация" },
        { id: "links", label: "Ссылки" },
        { id: "direct", label: "Директ РСЯ" },
      ]}
    >
      <section className="Memos-section" id="role">
        <h2>Retargeo</h2>
        <p>
          Селф-сервис: клиент сам запускает ретаргет и look-alike. Трафик — техподдержка: модерация,
          доп. трафик, оптимизация.
        </p>
        <p>
          На каждом баннере обязательны данные рекламодателя: «Реклама. Рекламодатель. Организация. ИНН».
          Дисклеймер — если ниша требует, например медицина.
        </p>
      </section>
      <section className="Memos-section" id="mod">
        <h2>Как модерировать</h2>
        <ol>
          <li>Уведомление в чате бота Retargeo: РК на модерации и почта создателя.</li>
          <li>Админка → Клиенты → Retargeo. Баланс должен быть положительным.</li>
          <li>Реквизиты из ЛК внесите в таблицу договоров Retargeo.</li>
          <li>Проверьте дисклеймер и ИНН на баннерах. Сохраните файлы, зарегистрируйте в ОРД, получите ЕРИД.</li>
          <li>В модерации вставьте ЕРИД, проверьте, одобрите. РК включится, если клиент её уже включил.</li>
        </ol>
        <Fig n={128} caption="Уведомление бота: кампания на модерации и почта создателя" />
        <Figs
          cols={2}
          items={[
            { n: 129, caption: "Клиенты Retargeo, проверка баланса" },
            { n: 130, caption: "Карточка клиента" },
            { n: 131, caption: "Реквизиты" },
            { n: 132, caption: "Объявления в РК" },
            { n: 133, caption: "Проверка баннера" },
            { n: 134, caption: "Рабочая РК после модерации" },
          ]}
        />
        <p>
          Узкое супергео, пол, возраст — чаще Директ. Инволтра одна обычно проигрывает: нет пола/возраста,
          только ПК. Миксуйте, план дневного бюджета закрывайте. На CPC деньги списываются с кликов.
          На CPM клики всё равно нужны для CTR.
        </p>
      </section>
      <section className="Memos-section" id="links">
        <h2>Ссылки для доп. трафика</h2>
        <p>
          Только из ЛК, на все объявления. На Retargeo для площадок ставим PRG4, PRG5, PRG7.
          Пример: Инволтра PRG4, Rich PRG5.
        </p>
        <Fig n={135} caption="«Получить итоговую ссылку» в объявлении Retargeo" />
        <CodeBlock
          label="Идея ссылки для Инволтры"
          value="https://посадочная/?utm_source=Retargeo&utm_medium=PRG4&utm_campaign=UUID_КАМПАНИИ&utm_content=UUID_БАННЕРА"
        />
        <CodeBlock
          label="Идея ссылки для Rich"
          value="https://посадочная/?utm_source=Retargeo&utm_medium=PRG5&utm_campaign=UUID_КАМПАНИИ&utm_content=UUID_БАННЕРА&utm_term=[PUBLISHER_ID]"
        />
        <p>
          Скрин и примеры выше в старой схеме (campaign/content = UUID). Новые объявления создаются
          с макросами в <code translate="no">utm_term</code> — берите фактическую строку из кнопки, не копируйте шаблон
          из этой памятки. Правила — в «Разметке».
        </p>
        <p>
          На Инволтре и Директе макрос сайта можно убрать. На Rich/Bid подставьте их макрос источника.
        </p>
      </section>
      <section className="Memos-section" id="direct">
        <h2>Директ для Retargeo</h2>
        <p>При создании РК обязательно уберите счётчик Метрики.</p>
        <Figs
          cols={2}
          items={[
            { n: 136, caption: "Старт создания РСЯ" },
            { n: 137, caption: "Снять счётчик Метрики" },
            { n: 138, caption: "Стратегия: лимит по бюджету, не по цене клика" },
            { n: 139, caption: "Автотаргетинг можно включить для объёма" },
            { n: 140, caption: "Отчёт по площадкам" },
            { n: 141, caption: "Фильтры отказов и качества" },
            { n: 142, caption: "Тип условия показа: автотаргет vs интересы" },
          ]}
        />
        <p>
          Режьте com/ru/dsp/vpn и игровые площадки, если CTR выше ~10% — часто мусор.
          RU с VK можно оставить, смотрите вторичку.
        </p>
        <Related
          items={[
            { to: "/admin/memos/markup", label: "Разметка" },
            { to: "/admin/memos/moderation", label: "ЕРИД" },
            { to: "/admin/memos/extra-traffic", label: "Доп. трафик" },
          ]}
        />
      </section>
    </GuideMemo>
  );
}

export function OrdMemo() {
  return (
    <GuideMemo
      sections={[
        { id: "start", label: "Кабинет" },
        { id: "creative", label: "Креатив" },
        { id: "chain", label: "Цепочки" },
        { id: "self", label: "Самореклама" },
      ]}
    >
      <section className="Memos-section" id="start">
        <h2>Медиаскаут</h2>
        <p>
          Клиент заходит через Программатик Груп или Бизнес Солюшенс. Креативы регистрируем в кабинете БС,
          исполнитель зависит от договора. Логины — в таблице доступов, сюда их не пишем.
        </p>
        <p>
          Рабочая зона: агентство Бизнес Солюшенс → доходный договор оказания услуг.
          Рекламодателя ищите по ИНН из «Новый ФЗ».
        </p>
        <Figs
          cols={2}
          items={[
            { n: 143, caption: "Контрагенты: выбрать Бизнес Солюшенс" },
            { n: 144, caption: "Договоры агентства" },
            { n: 145, caption: "Доходный договор оказания услуг" },
            { n: 146, caption: "Новый договор, если клиента ещё нет" },
          ]}
        />
      </section>
      <section className="Memos-section" id="creative">
        <h2>Регистрация баннера</h2>
        <p>
          «Добавить креатив». Тип РК — CPC или CPM из медиаплана. Форма — баннер или видео.
          Категория — по ОКВЭД из ФЗ. Целевая страница из ФЗ. Одинаковый креатив в ресайзах — один токен.
          Разные макеты — «Добавить креатив с другим токеном».
        </p>
        <Figs
          cols={2}
          items={[
            { n: 147, caption: "Карточка договора" },
            { n: 148, caption: "Добавить креатив" },
            { n: 149, caption: "Тип и форма распространения" },
            { n: 150, caption: "Загрузка файлов" },
            { n: 151, caption: "Один токен на пак или разные токены" },
            { n: 152, caption: "Новый контрагент, если клиента не было" },
          ]}
        />
        <p>Сумму договора не указываем.</p>
      </section>
      <section className="Memos-section" id="chain">
        <h2>Цепочки ОРД</h2>
        <div className="Memos-macros">
          <div>
            <h3>С агентством клиента</h3>
            <p>РД → РА1 (агентство клиента) → РА2 (мы: КД, БС, ПГ) → РР (паблишер)</p>
          </div>
          <div>
            <h3>Напрямую</h3>
            <p>РД → РА (мы) → РР. Чьи баннеры — того и регистрируем как РД.</p>
          </div>
        </div>
        <p>
          Исполнитель — ближайшее звено к клиенту. Если есть агентство клиента, исполнитель — оно.
          Столбцы «Новый ФЗ»: жёлтый РД, голубой агентство, фиолетовый договор между ними, оранжевый — договор с нами.
        </p>
        <Figs
          cols={2}
          items={[
            { n: 153, caption: "Изначальный договор при агентстве клиента" },
            { n: 154, caption: "Рекламодатель из жёлтого столбца" },
            { n: 155, caption: "Исполнитель — агентство клиента" },
            { n: 156, caption: "Данные договора" },
            { n: 157, caption: "Проверка цепочки" },
            { n: 158, caption: "Кабинет нашего агентства: контрагент" },
            { n: 159, caption: "Доходный договор" },
            { n: 160, caption: "Изначальный договор РА–РД" },
            { n: 161, caption: "Цепочка ПГ/КД → БС: добавить изначальный договор" },
          ]}
        />
        <p>
          В кабинетах агентства креативы не регистрируем — только договоры. Бюджеты ПГ и КД уходят на БС,
          это тоже отражается договором между юрлицами. Актуальные номера договоров смотрите в таблице,
          не из памяти.
        </p>
        <p>
          Блок «пока что неактуально» из старого гайда не используйте, пока аккаунтинг не подтвердит новую схему.
        </p>
      </section>
      <section className="Memos-section" id="self">
        <h2>Самореклама</h2>
        <p>
          Баннеры на нашей охватке — не самореклама: показы идут через сетки партнёров. Регистрируйте
          как обычную рекламу в расходный договор, где заказчик БС, а исполнитель — сетка. Тип оплаты CPM.
        </p>
        <Related
          items={[
            { to: "/admin/memos/moderation", label: "Модерация" },
            { to: "/admin/memos/direct", label: "Директ" },
          ]}
        />
      </section>
    </GuideMemo>
  );
}

export function PopMemo() {
  return (
    <GuideMemo
      sections={[
        { id: "bid", label: "Bid" },
        { id: "mybid", label: "MyBid" },
        { id: "rich", label: "Rich" },
        { id: "involta", label: "Инволтра" },
      ]}
    >
      <section className="Memos-section" id="bid">
        <h2>Bid</h2>
        <p>
          Самый ходовой канал, клик обычно дешёвый. PRG84. Логин — в таблице доступов.
          Manage Campaigns: Show ALL, фильтр Name Contains. Имена на английском, иногда капсом.
        </p>
        <Figs
          cols={2}
          items={[
            { n: 12, caption: "Поиск РК: Show ALL и Name Contains" },
            { n: 13, caption: "Create New Campaign, обычно Pop-Under" },
            { n: 14, caption: "Гео: сначала Россия, узкое гео ниже. Бриф или аккаунт" },
            { n: 15, caption: "Этот шаг оставляем как есть" },
            { n: 16, caption: "Старт цены часто 0,00350, потом крутите" },
            { n: 17, caption: "Следующий шаг формы" },
            { n: 18, caption: "Ссылка — из нашего ЛК" },
            { n: 19, caption: "Проверка полей" },
            { n: 20, caption: "Десктоп / мобайл можно выбрать ещё раз в конце" },
            { n: 21, caption: "Лимиты" },
            { n: 22, caption: "Сохранение" },
            { n: 23, caption: "Список созданных РК" },
            { n: 24, caption: "Редактирование" },
            { n: 25, caption: "Blacklist: ключи из Метрики, каждый с новой строки, Update" },
          ]}
        />
        <p>
          Оптимизация: шестерёнка у РК → blacklist → ключи источников, которые надо отрезать.
        </p>
      </section>
      <section className="Memos-section" id="mybid">
        <h2>MyBid</h2>
        <p>PRG80. Похож на Bid, проще в настройке, трафик дешевле и менее предсказуемый.</p>
        <ol>
          <li>Create campaign → Popunder.</li>
          <li>General по образцу, дневной бюджет на первый запуск лучше маленький.</li>
          <li>Таргет из ТЗ аккаунтов. Постоянные галочки — dch / mil / ses.</li>
          <li>В креатив — ссылка из ЛК. Не путайте порядок id кампании и баннера.</li>
          <li>После модерации площадки РК включится. Блеклист — во вкладке таргетинга.</li>
        </ol>
      </section>
      <section className="Memos-section" id="rich">
        <h2>RichAds</h2>
        <p>
          PRG81. Макрос источника — <code translate="no">[PUBLISHER_ID]</code> в term или в отдельном параметре,
          если так требует актуальная разметка. Создание РК — в их интерфейсе по тому же принципу: ссылка из ЛК,
          код PRG руками.
        </p>
      </section>
      <section className="Memos-section" id="involta">
        <h2>Инволтра</h2>
        <p>
          PRG8 на Programmatic. На Retargeo для неё берите PRG4. Макросов источника нет — кусок site id
          из ссылки можно убрать. Каждый месяц заполняется таблица метрики Инволтры и уходит Диме на оплату.
        </p>
        <Related
          items={[
            { to: "/admin/memos/extra-traffic", label: "Как взять ссылку" },
            { to: "/admin/memos/funding", label: "Пополнение" },
            { to: "/admin/memos/glossary", label: "Коды PRG" },
          ]}
        />
      </section>
    </GuideMemo>
  );
}

export function DspMemo() {
  return (
    <GuideMemo
      sections={[
        { id: "sape", label: "Sape" },
        { id: "get", label: "Getintent" },
        { id: "hybrid", label: "Hybrid" },
      ]}
    >
      <section className="Memos-section" id="sape">
        <h2>Sape</h2>
        <p>PRG86. Два кабинета (БС и ПГ) — логины в таблице доступов. В «Кампании» смотрят даты, показы и расход, либо в «Отчёты».</p>
        <Fig n={120} caption="Кампании Sape: даты, показы, расход, клики" />
      </section>
      <section className="Memos-section" id="get">
        <h2>Getintent</h2>
        <p>PRG87. DSP для медийки через RTB. Новый рекламодатель создаётся письмом в поддержку с почты жёлтого программатика, кнопка «Ответить всем».</p>
        <p>
          Ветки: создание рекламодателей и помощь в настройке РК. Файл-шаблон рекламодателя заполняют из «Новый ФЗ».
          В файле — изначальный договор: если клиент пришёл через агентство, это договор РД с этим агентством.
        </p>
        <Figs
          cols={2}
          items={[
            { n: 121, caption: "Рекламодатели Getintent" },
            { n: 122, caption: "Отчёты: даты, расширенный отчёт, фильтр по рекламодателю" },
            { n: 162, caption: "Шаблон данных для ОРД / создания рекламодателя" },
            { n: 163, caption: "Письмо, что рекламодатель создан" },
            { n: 164, caption: "Пакетная загрузка креативов" },
            { n: 165, caption: "ККТУ и автозаполнение полей" },
            { n: 166, caption: "Новая кампания, чаще баннеры для браузеров" },
            { n: 167, caption: "Бюджет, дневной лимит кликов, max CPM или fix CPM" },
            { n: 168, caption: "Креативы и регистрация в ОРД на стороне площадки" },
            { n: 169, caption: "Гео" },
            { n: 170, caption: "Максимизация CTR" },
            { n: 171, caption: "Исключить домены и роботов для ЯМ" },
            { n: 172, caption: "Узкое гео на Гете идёт плохо" },
            { n: 173, caption: "Группы кампаний для автолимитов" },
          ]}
        />
        <p>
          Баннеры лучше отправить на модерацию сразу: проверяют долго. Отклоняют из‑за ИНН на баннере
          или плохого качества. Средняя ставка около 40–45 ₽ за 1000. Узкий город почти не едет,
          лучше МСК / СПБ / РФ. Сломалось — ветка «Помощь в настройке РК».
        </p>
      </section>
      <section className="Memos-section" id="hybrid">
        <h2>Hybrid</h2>
        <p>
          PRG88. Рекламодателя создаём сами, договор с Гибридом и аккаунт — в таблице доступов.
          Коммуникация с поддержкой в их Telegram. Документация регистрации в ОРД — на сайте Hybrid.
        </p>
        <Figs
          cols={2}
          items={[
            { n: 123, caption: "Конструктор отчётов: рекламодатели и даты" },
            { n: 174, caption: "Регистрация рекламодателя" },
            { n: 175, caption: "Стратегия: показы, равномерно, фиксированный CPM с низкой ставки" },
            { n: 176, caption: "Инвентарь сначала весь, плохие сетки потом исключают" },
            { n: 177, caption: "Сегменты Hybrid бесплатные, свои берём только по запросу клиента" },
          ]}
        />
        <p>
          URL целевой страницы — наша ссылка на клик. Ссылка на показ сторонней системы — в счётчик показов.
          Старт CPM часто 20–25 ₽, если не едет — поднимаем.
        </p>
        <Related
          items={[
            { to: "/admin/memos/trackers", label: "Пиксели" },
            { to: "/admin/memos/ord", label: "ОРД" },
            { to: "/admin/memos/funding", label: "Пополнение" },
          ]}
        />
      </section>
    </GuideMemo>
  );
}

export function FundingMemo() {
  return (
    <GuideMemo
      sections={[
        { id: "rule", label: "Правило" },
        { id: "bid", label: "Bid и Rich" },
        { id: "involta", label: "Инволтра" },
      ]}
    >
      <section className="Memos-section" id="rule">
        <h2>Пополнение площадок</h2>
        <p>
          Сумму согласуйте заранее. Логины, кошельки и карты в памятку не кладём — только таблица доступов
          и сообщения Диме.
        </p>
        <div className="Memos-callout">
          <p>
            Сумму и адрес оплаты отправляйте Диме отдельными сообщениями. Чеки и подтверждения — тоже отдельно.
            Не пересылайте реквизиты в общий чат проекта.
          </p>
        </div>
      </section>
      <section className="Memos-section" id="bid">
        <h2>Bid и Rich</h2>
        <ol>
          <li>В кабинете площадки откройте пополнение (Current Balance / Add Funds).</li>
          <li>Custom amount — согласованная сумма. На Rich учитывайте НДС, сумму придётся подгонять.</li>
          <li>Оплата криптой: USDT, сеть TRON TRC-20.</li>
          <li>Скопированные сумму и адрес отдайте Диме. Зачисление обычно быстрое.</li>
        </ol>
        <p>Если у площадки окно оплаты не открылось — подождите и повторите шаг, не меняя сумму.</p>
      </section>
      <section className="Memos-section" id="involta">
        <h2>Инволтра</h2>
        <p>
          Раз в месяц заполняется таблица метрики Инволтры и отправляется Диме — по ней идёт оплата.
          Распределите заполнение между сотрудниками.
        </p>
        <p>Обмен рублей в USDT проводит Дима. Самостоятельные обменники и сохранённые пароли из старых гайдов не используйте.</p>
        <Related
          items={[
            { to: "/admin/memos/pop", label: "Bid и Rich" },
            { to: "/admin/memos/glossary", label: "Коды каналов" },
          ]}
        />
      </section>
    </GuideMemo>
  );
}
